import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import Badge from "@/components/ui/badge";
import Chip from "@/components/ui/chip";
import { Textarea } from "@/components/ui/textarea";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import {
  DreamhubGradient,
  InfoCircle,
  AlertTriangle
} from "@dreamhub/designsystem/icons";
import {
  useCreateAutofillDraftQuestion,
  useDeleteAutofillDraftQuestion,
  useEvaluateAutofillInstructions,
  useGenerateAutofillInstructions,
  AutofillAssessment,
  AutofillGeneratedFrom,
  DreamerFlow
} from "../hooks/useAutofillApi";
import { useAutofillTarget } from "../hooks/autofillTarget";
import {
  AUTOFILL_CARD_BACKGROUND,
  AUTOFILL_CONFIDENCE_BADGE,
  AUTOFILL_NO_TRIGGER_PATH,
  AUTOFILL_RENAME_GENERATE_DEBOUNCE_MS,
  AUTOFILL_TRIGGER_ACTIVITY_TYPES,
  AutofillConfidence,
  AutofillPromptOwner
} from "../const";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { TestAutofillPopover } from "./TestAutofillPopover";

interface AIAutofillSectionProps {
  fieldName: string;
  fieldType: string;
  helpText: string;
  questionnaireId: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onInstructionsChange?: (instructions: string | null) => void;
  onQuestionIdChange?: (questionId: string | null) => void;
  onDreamerFlowChange?: (dreamerFlow: DreamerFlow | null) => void;
  onSaveBlockedChange?: (blocked: boolean, tooltip?: string | null) => void;
  translationPrefix: string;
  className?: string;
  initialQuestionId?: string;
  /** The stored field's own name — the name its saved prompt was written for. */
  initialFieldName?: string;
  isExistingAutofill?: boolean;
  initialDreamerFlow?: DreamerFlow | null;
  promptOwner?: AutofillPromptOwner;
}

const CONFIDENCE_FROM_ASSESSMENT: Record<
  AutofillAssessment["confidence"],
  AutofillConfidence
> = {
  high: "strong",
  medium: "medium",
  low: "low"
};

const ASSESSMENT_FROM_CONFIDENCE: Record<
  AutofillConfidence,
  AutofillAssessment["confidence"]
> = {
  strong: "high",
  medium: "medium",
  low: "low"
};

export const AIAutofillSection: React.FC<AIAutofillSectionProps> = ({
  fieldName,
  fieldType,
  helpText,
  questionnaireId,
  enabled,
  onEnabledChange,
  onInstructionsChange,
  onQuestionIdChange,
  onDreamerFlowChange,
  onSaveBlockedChange,
  translationPrefix,
  className = "mt-6",
  initialQuestionId,
  initialFieldName,
  isExistingAutofill = false,
  initialDreamerFlow,
  promptOwner = "own"
}) => {
  const { t } = useTranslation();
  const createDraft = useCreateAutofillDraftQuestion();
  const deleteDraft = useDeleteAutofillDraftQuestion();
  const generate = useGenerateAutofillInstructions();
  const evaluate = useEvaluateAutofillInstructions();
  const target = useAutofillTarget();
  const { enabled: isTestFlagEnabled } = useFeatureFlag(
    FeatureFlags.CRM_7395_AUTOFILL_TEST
  );
  const isTestEnabled = !target.requiresTestFlag || isTestFlagEnabled;

  const isSystemOwned = promptOwner === "system";
  const isConverting = promptOwner === "converting";
  // Neither owner inherits the stored question's prompt: a system-owned field has no
  // admin prompt at all, and a converting one is authoring a prompt for a name the
  // stored question does not carry yet.
  const inheritsStoredPrompt = !isSystemOwned && !isConverting;
  const storedQuestionId = inheritsStoredPrompt ? initialQuestionId : undefined;
  const hasStoredAutofill = inheritsStoredPrompt && isExistingAutofill;
  const storedDreamerFlow = inheritsStoredPrompt ? initialDreamerFlow : null;

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [questionId, setQuestionId] = useState<string | null>(
    storedQuestionId ?? null
  );
  const draftCreatedRef = useRef(false);
  const [instructions, setInstructions] = useState("");
  const [defaultInstructions, setDefaultInstructions] = useState("");
  const [confidence, setConfidence] = useState<AutofillConfidence | null>(null);
  const [whatsMissing, setWhatsMissing] = useState<string[]>([]);
  const [generatedFrom, setGeneratedFrom] =
    useState<AutofillGeneratedFrom | null>(null);
  const [triggeringActivityTypes, setTriggeringActivityTypes] = useState<
    string[]
  >([]);
  const [dependentFieldKeys, setDependentFieldKeys] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [generatedFromName, setGeneratedFromName] = useState("");
  const [nameDecision, setNameDecision] = useState<
    "keep" | "regenerate" | null
  >(null);

  const trimmedName = fieldName.trim();
  const toggleDisabled = !trimmedName || !fieldType || !questionnaireId;
  const hasInstructions = confidence !== null;
  const nameChanged = hasInstructions && generatedFromName !== trimmedName;
  const pendingNameDecision = nameChanged && nameDecision === null;
  const isBusy =
    createDraft.isPending || generate.isPending || evaluate.isPending;

  const onInstructionsChangeRef = useRef(onInstructionsChange);
  const onQuestionIdChangeRef = useRef(onQuestionIdChange);
  const onDreamerFlowChangeRef = useRef(onDreamerFlowChange);
  const onSaveBlockedChangeRef = useRef(onSaveBlockedChange);
  const generateRequestRef = useRef(0);
  const questionIdRef = useRef<string | null>(null);
  const hydratedRef = useRef<string | null>(null);
  // Synced after every commit rather than during render: a render-time ref write is unsafe
  // under concurrent rendering (the render may be discarded or replayed). Event handlers
  // still write questionIdRef directly — that is an event-time write, which is fine.
  useEffect(() => {
    onInstructionsChangeRef.current = onInstructionsChange;
    onQuestionIdChangeRef.current = onQuestionIdChange;
    onDreamerFlowChangeRef.current = onDreamerFlowChange;
    onSaveBlockedChangeRef.current = onSaveBlockedChange;
  });
  useEffect(() => {
    questionIdRef.current = questionId;
  }, [questionId]);

  // Closing the drawer switches autofill off from outside, so a draft still being created
  // is stale and the generate path deletes it when it lands.
  useEffect(() => {
    if (!enabled) generateRequestRef.current += 1;
  }, [enabled]);
  useEffect(() => {
    const requests = generateRequestRef;
    return () => {
      requests.current += 1;
    };
  }, []);

  useEffect(() => {
    if (
      !hasStoredAutofill ||
      !storedDreamerFlow ||
      !storedQuestionId ||
      !trimmedName ||
      hydratedRef.current === storedQuestionId
    ) {
      return;
    }
    hydratedRef.current = storedQuestionId;
    const assessment = storedDreamerFlow.assessment;
    setQuestionId(storedQuestionId);
    setInstructions(storedDreamerFlow.instructions);
    setDefaultInstructions(storedDreamerFlow.instructions);
    setConfidence(
      assessment ? CONFIDENCE_FROM_ASSESSMENT[assessment.confidence] : null
    );
    setWhatsMissing(assessment?.whatsMissing ?? []);
    setGeneratedFrom({ ...storedDreamerFlow.generatedFrom, helpText: null });
    setTriggeringActivityTypes(storedDreamerFlow.triggeringActivityTypes);
    setDependentFieldKeys(storedDreamerFlow.dependentFieldKeys);
    // The drawer's form can still hold the previously opened field's name on this render,
    // so the stored field's own name is the one the saved prompt belongs to.
    setGeneratedFromName(initialFieldName?.trim() || trimmedName);
  }, [
    hasStoredAutofill,
    storedDreamerFlow,
    storedQuestionId,
    trimmedName,
    initialFieldName
  ]);

  const reportedInstructions = enabled && hasInstructions ? instructions : null;
  useEffect(() => {
    onInstructionsChangeRef.current?.(reportedInstructions);
  }, [reportedInstructions]);

  useEffect(() => {
    onQuestionIdChangeRef.current?.(enabled ? questionId : null);
  }, [enabled, questionId]);

  const dreamerFlow: DreamerFlow | null = useMemo(() => {
    if (!enabled || !confidence || !generatedFrom) {
      return null;
    }
    return {
      version: 1,
      locked: false,
      instructions,
      generatedFrom: {
        label: generatedFrom.label,
        displayType: generatedFrom.displayType,
        options: generatedFrom.options
      },
      assessment: {
        confidence: ASSESSMENT_FROM_CONFIDENCE[confidence],
        whatsMissing
      },
      triggeringActivityTypes,
      dependentFieldKeys
    };
  }, [
    enabled,
    confidence,
    generatedFrom,
    instructions,
    whatsMissing,
    triggeringActivityTypes,
    dependentFieldKeys
  ]);
  useEffect(() => {
    onDreamerFlowChangeRef.current?.(dreamerFlow);
  }, [dreamerFlow]);

  // A system-owned field has no prompt to be incomplete, so nothing here can block its save.
  const saveBlocked =
    !isSystemOwned &&
    enabled &&
    (isBusy ||
      pendingNameDecision ||
      isEditing ||
      !questionId ||
      // Realigning questionId above makes an existing field reachable with a
      // live question and no instructions (a regeneration that failed after
      // resetState cleared them). Exempting existing autofills here would let
      // that state save and null out the stored flow.
      !instructions.trim() ||
      confidence === "low");
  const addBlockTooltip =
    enabled && hasInstructions && !isEditing && confidence === "low"
      ? t(`${translationPrefix}.addFieldDisabledTooltip`)
      : null;
  useEffect(() => {
    onSaveBlockedChangeRef.current?.(saveBlocked, addBlockTooltip);
  }, [saveBlocked, addBlockTooltip]);

  const resetState = () => {
    generateRequestRef.current += 1;
    setQuestionId(null);
    setInstructions("");
    setDefaultInstructions("");
    setConfidence(null);
    setWhatsMissing([]);
    setGeneratedFrom(null);
    setTriggeringActivityTypes([]);
    setDependentFieldKeys([]);
    setIsEditing(false);
    setNameDecision(null);
    setGeneratedFromName("");
  };

  const lastTriggerHintId = useId();
  const selectedTriggerCount = triggeringActivityTypes.filter((type) =>
    (AUTOFILL_TRIGGER_ACTIVITY_TYPES as readonly string[]).includes(type)
  ).length;

  // automator reads an empty list as "every activity", so the last type stays selected.
  const toggleTriggerType = (type: string) => {
    const isSelected = triggeringActivityTypes.includes(type);
    if (isSelected && selectedTriggerCount <= 1) return;
    setTriggeringActivityTypes((current) =>
      isSelected
        ? current.filter((value) => value !== type)
        : [
            ...current.filter((value) => value !== AUTOFILL_NO_TRIGGER_PATH),
            type
          ]
    );
  };

  const discardDraft = () => {
    const id = questionIdRef.current;
    if (id && draftCreatedRef.current) {
      deleteDraft.mutate({ pathParams: { questionId: id } });
    }
  };

  const createDraftQuestion = async (): Promise<string | null> => {
    try {
      const result = await createDraft.mutateAsync({
        data: target.buildDraft({
          groupId: questionnaireId,
          name: trimmedName,
          fieldType,
          helpText: helpText.trim() || null
        })
      });
      return result.id;
    } catch (error) {
      console.error("Failed to create autofill draft question:", error);
      return null;
    }
  };

  // `forName` is the name the generated prompt belongs to, when it isn't the form's current one.
  const runGenerate = async (existingQuestionId?: string, forName?: string) => {
    const requestId = (generateRequestRef.current += 1);

    let activeQuestionId =
      existingQuestionId ?? questionIdRef.current ?? storedQuestionId ?? null;
    const createdNow = !activeQuestionId;
    if (!activeQuestionId) {
      activeQuestionId = await createDraftQuestion();
      if (!activeQuestionId) return;
      if (requestId !== generateRequestRef.current) {
        deleteDraft.mutate({ pathParams: { questionId: activeQuestionId } });
        return;
      }
      draftCreatedRef.current = true;
    }
    // Also runs when the id came from props: re-enabling the toggle clears
    // questionId, and the save gate stays latched unless state is realigned
    // with the question the generation actually ran against.
    setQuestionId(activeQuestionId);

    try {
      const result = await generate.mutateAsync({
        pathParams: { questionId: activeQuestionId },
        data: {}
      });
      if (requestId !== generateRequestRef.current) return;
      setDefaultInstructions(result.instructions);
      setInstructions(result.instructions);
      setConfidence(CONFIDENCE_FROM_ASSESSMENT[result.assessment.confidence]);
      setWhatsMissing(result.assessment.whatsMissing);
      setGeneratedFrom(result.generatedFrom);
      setTriggeringActivityTypes(result.triggeringActivityTypes);
      setDependentFieldKeys(result.dependentFieldKeys);
      setGeneratedFromName(forName ?? trimmedName);
      setNameDecision(null);
      setIsEditing(false);
    } catch (error) {
      if (requestId !== generateRequestRef.current) return;
      // A freshly created draft with no instructions is unusable — discard it
      // so it isn't orphaned and the user can't save a half-configured field.
      if (createdNow && activeQuestionId) {
        deleteDraft.mutate({ pathParams: { questionId: activeQuestionId } });
        resetState();
      }
      console.error("Failed to generate autofill instructions:", error);
    }
  };

  const handleToggle = (next: boolean) => {
    onEnabledChange(next);
    // A connector-filled field has no prompt of its own, so there is nothing to
    // generate or discard — the switch is the whole control, both ways.
    if (isSystemOwned) {
      return;
    }
    if (next) {
      runGenerate();
    } else {
      discardDraft();
      resetState();
    }
  };

  const runGenerateRef = useRef(runGenerate);
  useEffect(() => {
    runGenerateRef.current = runGenerate;
  });

  // Instructions are generated from the name stored on the question, so a draft created for
  // an earlier name cannot be reused — replace it with one carrying the current name. A real
  // question is left alone: renaming it is only committed on save.
  const regenerateForCurrentName = () => {
    setNameDecision("regenerate");
    if (!draftCreatedRef.current) {
      runGenerate(questionId ?? undefined);
      return;
    }
    discardDraft();
    draftCreatedRef.current = false;
    setQuestionId(null);
    questionIdRef.current = null;
    runGenerate();
  };

  // Renaming a connector-filled field makes it an ordinary one, which needs a prompt of its
  // own. Wait for the name to settle so a pause between words doesn't fire a request, and
  // attempt each distinct name once so a failing generate can't spin.
  const autoGeneratedForRef = useRef<string | null>(null);
  const needsPromptForNewName =
    isConverting && enabled && !hasInstructions && !isBusy && !!trimmedName;
  useEffect(() => {
    if (!needsPromptForNewName || autoGeneratedForRef.current === trimmedName) {
      return;
    }
    const timer = setTimeout(() => {
      autoGeneratedForRef.current = trimmedName;
      runGenerateRef.current();
    }, AUTOFILL_RENAME_GENERATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [needsPromptForNewName, trimmedName]);

  // A stored field can have autofill on with no prompt saved, which would leave it unsavable
  // until the switch is cycled — generate its prompt once instead, against the stored question.
  const promptGeneratedForStoredRef = useRef<string | null>(null);
  const needsPromptForStoredField =
    hasStoredAutofill &&
    !storedDreamerFlow &&
    !!storedQuestionId &&
    enabled &&
    !hasInstructions &&
    !isBusy &&
    !!trimmedName;
  useEffect(() => {
    if (
      !needsPromptForStoredField ||
      !storedQuestionId ||
      promptGeneratedForStoredRef.current === storedQuestionId
    ) {
      return;
    }
    promptGeneratedForStoredRef.current = storedQuestionId;
    // The form can still hold the previously opened field's name on this render; the prompt is
    // generated from the stored question, so it belongs to the stored field's own name.
    runGenerateRef.current(storedQuestionId, initialFieldName?.trim() || undefined);
  }, [needsPromptForStoredField, storedQuestionId, initialFieldName]);

  const discardDraftRef = useRef(discardDraft);
  const resetStateRef = useRef(resetState);
  useEffect(() => {
    discardDraftRef.current = discardDraft;
    resetStateRef.current = resetState;
  });

  // Crossing between owners invalidates whatever the previous one produced: a prompt drafted
  // for a new name is meaningless once the name is put back, and vice versa.
  const promptOwnerRef = useRef(promptOwner);
  useEffect(() => {
    if (promptOwnerRef.current === promptOwner) {
      return;
    }
    promptOwnerRef.current = promptOwner;
    discardDraftRef.current();
    resetStateRef.current();
    draftCreatedRef.current = false;
    autoGeneratedForRef.current = null;
    hydratedRef.current = null;
  }, [promptOwner]);

  const startEditing = () => {
    setDraft(instructions);
    setIsEditing(true);
  };

  const applyEdit = async () => {
    if (!questionId) return;
    try {
      const result = await evaluate.mutateAsync({
        pathParams: { questionId },
        data: { instructions: draft }
      });
      setInstructions(draft);
      setConfidence(CONFIDENCE_FROM_ASSESSMENT[result.assessment.confidence]);
      setWhatsMissing(result.assessment.whatsMissing);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to evaluate autofill instructions:", error);
    }
  };

  const tooltipMessage = !trimmedName
    ? t(`${translationPrefix}.disabledTooltip.fieldName`)
    : t(`${translationPrefix}.disabledTooltip.fieldType`);

  const renderToggle = () => {
    const toggle = (
      <Switch
        checked={enabled}
        onCheckedChange={handleToggle}
        disabled={toggleDisabled}
        size="lg"
      />
    );
    return toggleDisabled ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              aria-disabled="true"
              className="inline-flex cursor-not-allowed"
            >
              {toggle}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <Text variant="smBody" className="max-w-xs">
              {tooltipMessage}
            </Text>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      toggle
    );
  };

  const renderGeneratingState = () => (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Text variant="bodySecondary">
          {t(`${translationPrefix}.instructions.label`)}
        </Text>
        <Button variant="tertiary" disabled>
          {t(`${translationPrefix}.instructions.edit`)}
        </Button>
      </div>
      <Textarea
        value=""
        placeholder={t(`${translationPrefix}.generating`)}
        disabled
        rows={1}
        className="rounded-lg bg-surface-elevation2 border-[0.5px] border-border-border1"
      />
      <div className="flex items-center justify-between">
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin text-tertiary" />
        {isTestEnabled ? (
          <Button variant="secondary" disabled>
            {t(`${translationPrefix}.runTest`)}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const renderBody = () => {
    if (isBusy) {
      return renderGeneratingState();
    }

    if (!hasInstructions) {
      return null;
    }

    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Text variant="bodySecondary">
            {t(`${translationPrefix}.instructions.label`)}
          </Text>
          {isEditing ? null : (
            <Button variant="tertiary" onClick={startEditing}>
              {t(`${translationPrefix}.instructions.edit`)}
            </Button>
          )}
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-[110px] rounded-lg bg-surface-elevation2 border-[0.5px] border-border-border1"
            />
            <div className="flex items-center justify-between">
              <Button
                variant="tertiary"
                disabled={draft === defaultInstructions}
                onClick={() => setDraft(defaultInstructions)}
              >
                {t(`${translationPrefix}.instructions.resetToDefault`)}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setIsEditing(false)}
                >
                  {t("cancel")}
                </Button>
                <Button
                  disabled={evaluate.isPending}
                  onClick={applyEdit}
                >
                  {t(`${translationPrefix}.instructions.save`)}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg p-3">
            <Text variant="body">{instructions}</Text>
          </div>
        )}

        {nameChanged ? (
          <div className="flex flex-col gap-2 rounded-lg bg-surface-elevation2 p-3">
            <div className="flex items-start gap-2">
              <IconContainer size="sm" color="warning">
                <AlertTriangle />
              </IconContainer>
              <Text variant="smBodyWarning">
                {t(`${translationPrefix}.nameChanged.warning`)}
              </Text>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondaryNoBg"
                onClick={() => {
                  setNameDecision("keep");
                  setGeneratedFromName(trimmedName);
                }}
              >
                {t(`${translationPrefix}.nameChanged.keepCurrent`)}
              </Button>
              <Button onClick={regenerateForCurrentName}>
                {t(`${translationPrefix}.nameChanged.regenerate`)}
              </Button>
            </div>
          </div>
        ) : confidence ? (
          <div className="flex flex-col gap-3">
            <div className="flex">
              <Badge
                text={t(`${translationPrefix}.confidence.${confidence}`)}
                textVariant="label"
                className={cn(
                  "border-none gap-0 rounded-md h-7 whitespace-nowrap px-[10px] py-[6px]",
                  AUTOFILL_CONFIDENCE_BADGE[confidence].background
                )}
                textClassName={AUTOFILL_CONFIDENCE_BADGE[confidence].textColor}
              />
            </div>

            {confidence === "low" ? (
              <div className="flex items-start gap-2 rounded-lg bg-surface-elevation2 p-3">
                <IconContainer size="sm" color="secondary">
                  <InfoCircle />
                </IconContainer>
                <Text variant="smBodySecondary">
                  {t(`${translationPrefix}.lowConfidenceInfo`)}
                </Text>
              </div>
            ) : null}

            {whatsMissing.length > 0 ? (
              <div className="flex flex-col gap-1">
                <Text variant="smLabelSecondary">
                  {t(`${translationPrefix}.whatsMissing.title`)}
                </Text>
                <ul className="flex flex-col gap-1 list-disc pl-5">
                  {whatsMissing.map((item) => (
                    <li key={item}>
                      <Text variant="smBodySecondary" as="span">
                        {item}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {target.triggerTypesEditable ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <Text variant="smLabelSecondary">
                    {t(`${translationPrefix}.triggers.title`)}
                  </Text>
                  <Text variant="smBodySecondary">
                    {t(`${translationPrefix}.triggers.hint`)}
                  </Text>
                </div>
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label={t(`${translationPrefix}.triggers.title`)}
                >
                  {AUTOFILL_TRIGGER_ACTIVITY_TYPES.map((type) => {
                    const isSelected = triggeringActivityTypes.includes(type);
                    const isLastSelected =
                      isSelected && selectedTriggerCount <= 1;
                    return (
                      <Chip
                        key={type}
                        size="md"
                        filled
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-disabled={isLastSelected || undefined}
                        aria-describedby={
                          isLastSelected ? lastTriggerHintId : undefined
                        }
                        tabIndex={0}
                        selected={isSelected}
                        label={t(`${translationPrefix}.triggers.types.${type}`)}
                        onClick={() => toggleTriggerType(type)}
                        onKeyDown={(event) => {
                          if (event.key === " " || event.key === "Enter") {
                            event.preventDefault();
                            toggleTriggerType(type);
                          }
                        }}
                      />
                    );
                  })}
                </div>
                {/* A line, not a tooltip: an open tooltip covers the neighbouring chips. */}
                {selectedTriggerCount <= 1 ? (
                  <Text id={lastTriggerHintId} variant="smBodySecondary">
                    {t(`${translationPrefix}.triggers.lastOneHint`)}
                  </Text>
                ) : null}
              </div>
            ) : null}

            {isTestEnabled && questionId ? (
              <div className="flex justify-end">
                {confidence === "low" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          aria-disabled="true"
                          className="inline-flex cursor-not-allowed"
                        >
                          <Button variant="secondary" disabled>
                            {t(`${translationPrefix}.runTest`)}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <Text variant="smBody" className="max-w-xs">
                          {t(`${translationPrefix}.runTestDisabledTooltip`)}
                        </Text>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Popover
                    open={isTestModalOpen}
                    onOpenChange={setIsTestModalOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button variant="secondary">
                        {t(`${translationPrefix}.runTest`)}
                      </Button>
                    </PopoverTrigger>
                    <TestAutofillPopover
                      questionId={questionId}
                      fieldName={fieldName}
                      fieldType={fieldType}
                      options={generatedFrom?.options ?? []}
                      instructions={instructions}
                      triggeringActivityTypes={triggeringActivityTypes}
                      translationPrefix={translationPrefix}
                      onClose={() => setIsTestModalOpen(false)}
                    />
                  </Popover>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  // Gating render as well as hydration covers the frame between a name being put back and
  // the owner-change effect clearing the prompt it produced.
  const showContent = !isSystemOwned && enabled && (isBusy || hasInstructions);

  return (
    <div className={className}>
      <div className="flex flex-col w-full gap-1">
        <Text variant="bodySecondary" className="pl-1">
          {t(`${translationPrefix}.title`)}
        </Text>
        <div
          className="flex flex-col w-full overflow-hidden rounded-xl border border-border-border1"
          style={{ background: AUTOFILL_CARD_BACKGROUND }}
        >
          <div
            className={cn(
              "flex items-center justify-between px-4 py-3",
              showContent && "border-b border-border-border1"
            )}
          >
            <div className="flex items-center gap-2">
              <DreamhubGradient />
              <Text variant="body">{t(`${translationPrefix}.label`)}</Text>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t(
                        `${translationPrefix}.${target.copyKeys.tooltip}`
                      )}
                      className="inline-flex cursor-pointer"
                    >
                      <IconContainer size="sm" color="secondary">
                        <InfoCircle />
                      </IconContainer>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Text variant="smBody" className="max-w-xs">
                      {t(`${translationPrefix}.${target.copyKeys.tooltip}`)}
                    </Text>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {renderToggle()}
          </div>
          {showContent ? (
            <div className="flex flex-col gap-4 p-4">{renderBody()}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
